/**
 * Cake in a Can — Core Theme JavaScript
 * Custom elements, focus trap, header scroll, mobile menu
 */

class FocusTrap {
  constructor(container) {
    this.container = container;
    this.focusable = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  activate() {
    this.container.addEventListener('keydown', this.handleKeyDown);
    const first = this.container.querySelector(this.focusable);
    if (first) first.focus();
  }

  deactivate() {
    this.container.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown(e) {
    if (e.key !== 'Tab') return;
    const elements = [...this.container.querySelectorAll(this.focusable)];
    if (!elements.length) return;
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

/* Quantity Selector */
class QuantitySelector extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.qty-input');
    this.minus = this.querySelector('.qty-btn--minus');
    this.plus = this.querySelector('.qty-btn--plus');
    this.min = parseInt(this.dataset.min || '1', 10);
    this.max = this.dataset.max ? parseInt(this.dataset.max, 10) : null;

    this.minus?.addEventListener('click', () => this.change(-1));
    this.plus?.addEventListener('click', () => this.change(1));
    this.input?.addEventListener('change', () => this.validate());
    this.validate();
  }

  change(delta) {
    const val = parseInt(this.input.value, 10) || 0;
    let next = val + delta;
    if (next < this.min) next = this.min;
    if (this.max !== null && next > this.max) next = this.max;
    this.input.value = next;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.validate();
  }

  validate() {
    let val = parseInt(this.input.value, 10) || this.min;
    if (val < this.min) val = this.min;
    if (this.max !== null && val > this.max) val = this.max;
    this.input.value = val;
    if (this.minus) this.minus.disabled = val <= this.min;
    if (this.plus) this.plus.disabled = this.max !== null && val >= this.max;
  }
}

/* Mobile Menu */
class MobileMenu extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.mobile-menu__panel');
    this.overlay = this.querySelector('.mobile-menu__overlay');
    this.trap = new FocusTrap(this.panel);
    this.close = this.close.bind(this);

    this.querySelectorAll('[data-menu-close]').forEach((el) => {
      el.addEventListener('click', this.close);
    });
    this.overlay?.addEventListener('click', this.close);
    this.querySelectorAll('a[href]').forEach((link) => {
      link.addEventListener('click', () => this.close());
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });
  }

  open() {
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-scroll-locked');
    this.trap.activate();
    document.dispatchEvent(new CustomEvent('mobilemenu:open'));
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-scroll-locked');
    this.trap.deactivate();
    document.dispatchEvent(new CustomEvent('mobilemenu:close'));
  }
}

/* FAQ Accordion — enhances native details */
class FaqAccordion extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('details').forEach((detail) => {
      detail.addEventListener('toggle', () => {
        if (detail.open) {
          this.querySelectorAll('details[open]').forEach((other) => {
            if (other !== detail) other.open = false;
          });
        }
      });
    });
  }
}

/* Reset stuck overlays after navigation / bfcache restore */
function resetPageState() {
  document.body.classList.remove('is-scroll-locked');

  const menu = document.querySelector('mobile-menu');
  if (menu?.classList.contains('is-open')) {
    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
  }

  const drawer = document.querySelector('cart-drawer');
  if (drawer?.classList.contains('is-open')) {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
  }
}

/* Header scroll behavior */
function initHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const update = () => {
    header.classList.toggle('is-scrolled', window.scrollY > 50);
  };

  window.addEventListener('scroll', update, { passive: true });
  update();
}

/* Contact page — fixed sidebar on desktop */
function initContactSidebar() {
  const aside = document.querySelector('.contact-page__aside');
  const card = aside?.querySelector('[data-contact-sidebar]');
  const grid = document.querySelector('.contact-page__grid');
  const header = document.querySelector('.site-header');
  if (!aside || !card || !grid) return;

  const mq = window.matchMedia('(min-width: 768px)');
  let frame = 0;

  const getOffset = () => (header ? Math.ceil(header.getBoundingClientRect().bottom + 16) : 96);

  const reset = () => {
    card.classList.remove('is-fixed', 'is-pinned-bottom');
    card.style.top = '';
    card.style.left = '';
    card.style.width = '';
    card.style.maxHeight = '';
    aside.style.minHeight = '';
  };

  const layout = () => {
    if (!mq.matches) {
      reset();
      return;
    }

    const offset = getOffset();
    const asideRect = aside.getBoundingClientRect();
    const gridRect = grid.getBoundingClientRect();
    const cardHeight = card.offsetHeight;
    const maxHeight = Math.max(window.innerHeight - offset - 16, 120);

    if (asideRect.top > offset) {
      reset();
      return;
    }

    aside.style.minHeight = `${cardHeight}px`;

    if (gridRect.bottom <= offset + cardHeight) {
      card.classList.remove('is-fixed');
      card.classList.add('is-pinned-bottom');
      card.style.top = '';
      card.style.left = '';
      card.style.width = '';
      card.style.maxHeight = '';
      return;
    }

    card.classList.remove('is-pinned-bottom');
    card.classList.add('is-fixed');
    card.style.top = `${offset}px`;
    card.style.left = `${asideRect.left}px`;
    card.style.width = `${asideRect.width}px`;
    card.style.maxHeight = `${maxHeight}px`;
  };

  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(layout);
  };

  mq.addEventListener('change', schedule);
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  new ResizeObserver(schedule).observe(card);
  new ResizeObserver(schedule).observe(grid);
  schedule();
}

/* Mobile menu toggle */
function initMenuToggle() {
  const btn = document.querySelector('[data-menu-open]');
  const menu = document.querySelector('mobile-menu');
  if (!btn || !menu) return;
  btn.addEventListener('click', () => menu.open());
}

/* ZIP code checker */
function initZipChecker() {
  const form = document.querySelector('[data-zip-checker]');
  if (!form) return;

  const input = form.querySelector('input');
  const result = document.querySelector('[data-zip-result]');
  const eligible = (form.dataset.eligibleZips || '').split(',').map((z) => z.trim()).filter(Boolean);

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const zip = input.value.trim().replace(/\D/g, '').slice(0, 5);
    if (!zip || !result) return;

    const isEligible = eligible.includes(zip);
    result.hidden = false;
    result.className = `zip-checker__result zip-checker__result--${isEligible ? 'success' : 'error'}`;
    result.textContent = isEligible
      ? (form.dataset.successMsg || 'Great news — we deliver to your area!')
      : (form.dataset.failureMsg || 'Sorry, that ZIP is outside our delivery area.');
  });
}

/* Announcement bar rotation */
function initAnnouncementBar() {
  const bar = document.querySelector('.announcement-bar');
  if (!bar) return;

  const messages = [...bar.querySelectorAll('.announcement-bar__message')];
  if (!messages.length) return;

  if (!messages.some((message) => message.classList.contains('is-active'))) {
    messages[0].classList.add('is-active');
  }

  if (!bar.hasAttribute('data-announcement-rotate') || messages.length <= 1) return;

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) return;

  let current = messages.findIndex((message) => message.classList.contains('is-active'));
  if (current < 0) current = 0;

  setInterval(() => {
    messages[current].classList.remove('is-active');
    current = (current + 1) % messages.length;
    messages[current].classList.add('is-active');
  }, 5000);
}

/* Hero background slideshow */
function initHeroSlideshow() {
  const hero = document.querySelector('[data-hero-slideshow]');
  if (!hero) return;

  const slides = [...hero.querySelectorAll('.hero__slide')];
  const dotGroups = [...hero.querySelectorAll('.hero__dots')].map((list) => [
    ...list.querySelectorAll('[data-hero-dot]'),
  ]);
  if (slides.length <= 1) return;

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const interval = (parseInt(hero.dataset.interval, 10) || 5) * 1000;
  let current = 0;
  let timer;

  const goTo = (index) => {
    current = (index + slides.length) % slides.length;
    slides.forEach((slide, i) => slide.classList.toggle('is-active', i === current));
    dotGroups.forEach((dots) => {
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === current);
        dot.setAttribute('aria-selected', i === current ? 'true' : 'false');
      });
    });
  };

  const next = () => goTo(current + 1);

  const start = () => {
    if (prefersReduced) return;
    stop();
    timer = setInterval(next, interval);
  };

  const stop = () => {
    if (timer) clearInterval(timer);
  };

  dotGroups.flat().forEach((dot) => {
    dot.addEventListener('click', () => {
      goTo(parseInt(dot.dataset.heroDot, 10));
      start();
    });
  });

  hero.addEventListener('mouseenter', stop);
  hero.addEventListener('mouseleave', start);
  hero.addEventListener('focusin', stop);
  hero.addEventListener('focusout', start);

  start();
}

/* About page photo slideshows (1–3 photos per chapter) */
function initAboutSlideshows() {
  document.querySelectorAll('[data-about-slideshow]').forEach((root) => {
    const slides = [...root.querySelectorAll('[data-about-slide]')];
    if (slides.length < 2) return;

    const dots = [...root.querySelectorAll('.about-slideshow__dot')];
    const prev = root.querySelector('[data-about-prev]');
    const next = root.querySelector('[data-about-next]');
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

    const show = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    };

    prev?.addEventListener('click', () => show(index - 1));
    next?.addEventListener('click', () => show(index + 1));
  });
}

/* Register custom elements */
customElements.define('quantity-selector', QuantitySelector);
customElements.define('mobile-menu', MobileMenu);
customElements.define('faq-accordion', FaqAccordion);

/* Init on DOM ready */
document.addEventListener('DOMContentLoaded', () => {
  resetPageState();
  initHeader();
  initContactSidebar();
  initMenuToggle();
  initZipChecker();
  initAnnouncementBar();
  initHeroSlideshow();
  initAboutSlideshows();
});

window.addEventListener('pageshow', resetPageState);

/* Export for other modules */
window.CakeTheme = { FocusTrap };
